<template>
  <div class="py-6">
    <div class="relative max-w-7xl px-4 sm:px-6 md:px-8">
      <ListHeader :title="t('menu.siteSubscribers')" :enableSearch="false">
        <router-link
          to="/site-subscribers/new"
          class="ml-auto inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-xs hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:outline-hidden"
        >
          {{ t('siteSubscribers.createNew') }}
        </router-link>
      </ListHeader>

      <ErrorMessage
        v-if="siteSubscriberListStore.error"
        :error="siteSubscriberListStore.error"
      />

      <div class="absolute top-11 mb-8 w-64 md:top-0 md:right-30 md:w-72">
        <div class="flex grow flex-col gap-x-2 lg:flex-row lg:items-center">
          <label
            for="sites"
            class="sr-only text-sm font-medium text-gray-700"
            >{{ t('jobAlerts.search.site') }}</label
          >

          <Multiselect
            id="sites"
            ref="multiselect"
            v-model="site"
            :options="searchSite"
            :filterResults="false"
            :noOptionsText="t('vacancy.company.noOptions')"
            :placeholder="t('jobAlerts.search.sitePlaceholder')"
            :resolveOnLoad="true"
            :minChars="0"
            :delay="500"
            :searchable="true"
            :object="true"
            valueProp="id"
            label="name"
            trackBy="name"
            inputType="search"
            autocomplete="off"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
        </div>
      </div>

      <div
        v-if="!isEmpty"
        class="mt-5 mb-5 flex flex-wrap gap-6 rounded-md pl-0.5 md:flex-row"
      >
        <div class="flex w-full items-center gap-2 md:w-auto">
          <div class="flex items-center gap-2">
            <input
              id="excludeTestCheck"
              :checked="siteSubscriberListStore.includeArchived"
              type="checkbox"
              @change="toggleIncludeArchived"
            />
            <label
              for="excludeTestCheck"
              class="inline-flex items-center gap-1 text-sm font-medium text-gray-700"
            >
              {{ t('include') }}
              <span
                class="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800"
                >{{ t('archived') }}</span
              ></label
            >
          </div>
        </div>
      </div>

      <List
        v-slot="{ entity: subscriber }"
        :entities="siteSubscriberListStore.siteSubscribers"
        :page="siteSubscriberListStore.page"
        :loading="siteSubscriberListStore.isLoading"
        :updatePageNumber="updatePageNumber"
        :updatePageSize="updatePageSize"
      >
        <router-link
          :to="`/site-subscribers/${subscriber.id}`"
          class="block text-indigo-600 hover:bg-gray-50 focus:underline focus:outline-hidden"
        >
          <div class="px-4 py-4 sm:px-6">
            <div class="flex items-center justify-between">
              <div>
                <div class="flex gap-x-2">
                  <p class="truncate text-sm font-medium text-indigo-600">
                    {{ subscriber.name }}
                  </p>
                  <span
                    v-if="subscriber.archived"
                    class="ml-0 inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800 xl:ml-auto"
                    >{{ t('archived') }}</span
                  >
                </div>
                <p class="mt-1 truncate text-xs text-gray-500">
                  {{ subscriber.site.url }}
                </p>
              </div>

              <div class="ml-2 flex shrink-0 gap-2 text-sm text-gray-500">
                <span>{{ t('siteSubscribers.publicationScopes') }}</span>
                <div class="flex items-center gap-1">
                  <span>{{ t('siteSubscribers.external') }}:</span>
                  <CheckIcon
                    v-if="subscriber.showExternal"
                    class="h-5 w-5 text-green-700"
                  />
                  <XMarkIcon v-else class="h-5 w-5 text-red-700" />
                </div>
                <div class="flex items-center gap-1">
                  <span>{{ t('siteSubscribers.regional') }}:</span>
                  <CheckIcon
                    v-if="subscriber.showRegional"
                    class="h-5 w-5 text-green-700"
                  />
                  <XMarkIcon v-else class="h-5 w-5 text-red-700" />
                </div>
                <div class="flex items-center gap-1">
                  <span>{{ t('siteSubscribers.internal') }}:</span>
                  <CheckIcon
                    v-if="subscriber.showInternal"
                    class="h-5 w-5 text-green-700"
                  />
                  <XMarkIcon v-else class="h-5 w-5 text-red-700" />
                </div>
              </div>
            </div>
          </div>
        </router-link>
      </List>
    </div>
  </div>
</template>

<script setup>
import ListHeader from '../components/ListHeader.vue'
import List from '../components/List.vue'
import ErrorMessage from '../components/ErrorMessage.vue'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSiteSubscriberListStore } from '../store/siteSubscriberListStore'
import Multiselect from '@vueform/multiselect'
import tooManagement from '../api/tooManagement'
import { CheckIcon, XMarkIcon } from '@heroicons/vue/20/solid'

const { t } = useI18n({
  inheritLocale: true,
  useScope: 'global',
})
const siteSubscriberListStore = useSiteSubscriberListStore()
const isEmpty = computed(
  () =>
    !siteSubscriberListStore.isLoading &&
    siteSubscriberListStore.filter === '' &&
    siteSubscriberListStore.vacancies.length === 0,
)

siteSubscriberListStore.load()

const site = ref({})
const searchSite = async (filter = '') => {
  const queryRes = await tooManagement.getSites({
    filter: filter || '',
    size: 15,
  })
  return queryRes.data
}

const updatePageNumber = (number) => {
  siteSubscriberListStore.page.number = number
  siteSubscriberListStore.load()
}
const updatePageSize = (pageSize) => {
  siteSubscriberListStore.page.size = pageSize
  siteSubscriberListStore.load()
}
const toggleIncludeArchived = () => {
  siteSubscriberListStore.includeArchived =
    !siteSubscriberListStore.includeArchived
  siteSubscriberListStore.page.number = 1
  siteSubscriberListStore.load()
}

watch(site, async (newSite) => {
  siteSubscriberListStore.siteId = newSite?.id || ''
  siteSubscriberListStore.page.number = 1
  siteSubscriberListStore.load()
})
</script>
